import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  dataTestId?: string;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  showCloseButton = true,
  maxWidth = 'md',
  dataTestId,
}) => {
  // Handle escape key press
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return (
    <div
      data-testid='modal-backdrop'
      className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 modal-backdrop'
      onClick={onClose}
    >
      <div
        data-testid={dataTestId}
        className={clsx(
          'relative bg-white rounded-lg shadow-xl w-full',
          'max-h-[90vh] flex flex-col',
          maxWidthClasses[maxWidth]
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className='flex items-center justify-between p-6 border-b border-gray-200'>
            {title && <h2 className='text-xl font-semibold text-gray-900'>{title}</h2>}
            {showCloseButton && (
              <button
                type='button'
                data-testid='close-dialog-button'
                onClick={onClose}
                className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'
                aria-label='Close modal'
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-6'>{children}</div>

        {/* Footer */}
        {footer && <div className='p-6 border-t border-gray-200'>{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
